import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, unwrap } from '../../data/supabase';
import { useAircraft, useAircraftTypes } from '../../data/queries';
import type { AnalyticsGoalRow, AnalyticsTailMonthRow, AnalyticsUploadRow, FlightLegRow } from '../../data/database.types';
import type { Dataset, Fleet } from './metrics';
import type { Trip } from './tripMetrics';
import type { TripFinanceRow } from '../../data/database.types';
import { usePortal } from '../../app/AuthProvider';

const PAGE = 5000;

/** Every row of a large table, a page at a time, so the API's row cap never truncates a year. */
async function fetchAll<T>(build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const res = await build(from, from + PAGE - 1);
    if (res.error) throw new Error(res.error.message);
    const rows = res.data ?? [];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

export const analyticsKeys = {
  legs: (orgId: string) => ['flight_legs', orgId] as const,
  availability: (orgId: string) => ['aircraft_availability', orgId] as const,
  booked: (orgId: string) => ['booked_legs', orgId] as const,
  goals: (orgId: string) => ['analytics_goals', orgId] as const,
  uploads: (orgId: string) => ['analytics_uploads', orgId] as const,
  tailMonths: (orgId: string) => ['analytics_tail_months', orgId] as const,
  trips: (orgId: string) => ['trip_finances', orgId] as const,
};

type LegLite = Pick<FlightLegRow, 'trip' | 'flight_date' | 'aircraft_id' | 'flight_hrs' | 'block_hrs' | 'trip_type' | 'faa_part' | 'customer_type' | 'organization' | 'customer' | 'orig' | 'dest'>;

/**
 * The whole dataset the metrics module works from (V2 D91): a few thousand
 * legs, one row per tail per day of availability, this month's bookings,
 * the goals, and the fleet. Small enough to hold in memory and recompute
 * on every filter change, which is what keeps every page in agreement.
 */
export function useAnalyticsDataset(orgId: string): { data: Dataset | null; loading: boolean; error: Error | null } {
  const aircraft = useAircraft(orgId);
  const types = useAircraftTypes(orgId);
  const legs = useQuery({
    queryKey: analyticsKeys.legs(orgId),
    staleTime: 60_000,
    queryFn: () =>
      fetchAll<LegLite>((from, to) =>
        supabase.from('flight_legs').select('trip, flight_date, aircraft_id, flight_hrs, block_hrs, trip_type, faa_part, customer_type, organization, customer, orig, dest').eq('org_id', orgId).order('flight_date').range(from, to)
      ),
  });
  const availability = useQuery({
    queryKey: analyticsKeys.availability(orgId),
    staleTime: 60_000,
    queryFn: () => fetchAll<{ aircraft_id: string; day: string; available: boolean }>((from, to) => supabase.from('aircraft_availability').select('aircraft_id, day, available').eq('org_id', orgId).order('day').range(from, to)),
  });
  const booked = useQuery({
    queryKey: analyticsKeys.booked(orgId),
    staleTime: 60_000,
    queryFn: async () => unwrap(await supabase.from('booked_legs').select('aircraft_id, flight_date, flight_hrs').eq('org_id', orgId)),
  });
  const goals = useQuery({
    queryKey: analyticsKeys.goals(orgId),
    staleTime: 60_000,
    queryFn: async () => unwrap(await supabase.from('analytics_goals').select('*').eq('org_id', orgId).order('effective_from')),
  });

  const tailMonths = useQuery({
    queryKey: analyticsKeys.tailMonths(orgId),
    staleTime: 60_000,
    queryFn: async () => unwrap(await supabase.from('analytics_tail_months').select('month, tails').eq('org_id', orgId).order('month')) as Pick<AnalyticsTailMonthRow, 'month' | 'tails'>[],
  });

  const data = useMemo<Dataset | null>(() => {
    if (!aircraft.data || !types.data || !legs.data || !availability.data || !booked.data || !goals.data || !tailMonths.data) return null;
    const typeCode = new Map(types.data.map((t) => [t.id, t.code]));
    const tailOf = new Map(aircraft.data.map((a) => [a.id, a.tail]));
    const fleet: Fleet[] = aircraft.data.map((a) => ({ id: a.id, tail: a.tail, type: typeCode.get(a.type_id) ?? '', active: a.active })).sort((a, b) => a.tail.localeCompare(b.tail));
    const typeOfTail = new Map(fleet.map((f) => [f.tail, f.type]));
    return {
      fleet,
      tailMonths: tailMonths.data.map((t) => ({ month: t.month, tails: Number(t.tails) })),
      goals: goals.data.map((g: AnalyticsGoalRow) => ({ effectiveFrom: g.effective_from, monthlyHoursPerTail: Number(g.monthly_hours_per_tail), targetHoursPerAvailableDay: Number(g.target_hours_per_available_day), availabilityTarget: Number(g.availability_target) })),
      legs: legs.data.flatMap((l) => {
        const tail = tailOf.get(l.aircraft_id);
        return tail ? [{ trip: l.trip, date: l.flight_date, tail, type: typeOfTail.get(tail) ?? '', flightHrs: Number(l.flight_hrs), blockHrs: Number(l.block_hrs), tripType: l.trip_type, faaPart: l.faa_part, customerType: l.customer_type, organization: l.organization, customer: l.customer, orig: l.orig, dest: l.dest }] : [];
      }),
      availability: availability.data.flatMap((a) => {
        const tail = tailOf.get(a.aircraft_id);
        return tail ? [{ tail, day: a.day, available: a.available }] : [];
      }),
      booked: booked.data.flatMap((b) => {
        const tail = tailOf.get(b.aircraft_id);
        return tail ? [{ tail, date: b.flight_date, flightHrs: Number(b.flight_hrs) }] : [];
      }),
    };
  }, [aircraft.data, types.data, legs.data, availability.data, booked.data, goals.data, tailMonths.data]);

  const first = [aircraft, types, legs, availability, booked, goals, tailMonths].find((q) => q.error);
  return { data, loading: !data && !first, error: (first?.error as Error | undefined) ?? null };
}

export function useAnalyticsUploads(orgId: string) {
  return useQuery({
    queryKey: analyticsKeys.uploads(orgId),
    queryFn: async () => unwrap(await supabase.from('analytics_uploads').select('*').eq('org_id', orgId).order('uploaded_at', { ascending: false }).limit(20)) as AnalyticsUploadRow[],
  });
}

/** The latest upload, for freshness lines on the home tile and the Overview. */
export function useLatestUpload(orgId: string, enabled = true) {
  return useQuery({
    queryKey: [...analyticsKeys.uploads(orgId), 'latest'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const rows = unwrap(await supabase.from('analytics_uploads').select('uploaded_at, range_end').eq('org_id', orgId).eq('kind', 'bulk').order('uploaded_at', { ascending: false }).limit(1)) as Pick<AnalyticsUploadRow, 'uploaded_at' | 'range_end'>[];
      return rows[0] ?? null;
    },
  });
}

/** Full leg rows for the Legs page: only the range asked for, every column. */
export function useFlightLegs(orgId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['flight_legs_full', orgId, from, to],
    staleTime: 60_000,
    queryFn: () => fetchAll<FlightLegRow>((a, b) => supabase.from('flight_legs').select('*').eq('org_id', orgId).gte('flight_date', from).lte('flight_date', to).order('flight_date').order('out_time').range(a, b)),
  });
}

/** JetInsight's trip finances (D109), with each trip's type from the fleet. */
export function useTripFinances(fleet: { id: string; tail: string; type: string }[]) {
  const { org } = usePortal();
  const q = useQuery({
    queryKey: analyticsKeys.trips(org.id),
    staleTime: 60_000,
    queryFn: () => fetchAll<TripFinanceRow>((a, b) => supabase.from('trip_finances').select('*').eq('org_id', org.id).order('trip_start').range(a, b)),
  });
  const data = useMemo<Trip[] | null>(() => {
    if (!q.data) return null;
    const typeOf = new Map(fleet.map((f) => [f.id, f.type]));
    return q.data.map((r) => ({
      trip: r.trip, booking: r.booking, customer: r.customer, customerType: r.customer_type, organization: r.organization, salesperson: r.salesperson,
      start: r.trip_start, tail: r.aircraft, type: (r.aircraft_id && typeOf.get(r.aircraft_id)) || '',
      liveBlock: Number(r.live_block), liveFlight: Number(r.live_flight), totalBlock: Number(r.total_block), status: r.status, price: Number(r.price), route: r.full_route ?? '',
    }));
  }, [q.data, fleet]);
  return { data, error: q.error as Error | null };
}
